import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import { fetchServices } from "../services/serviceService";
import { param, useListUrlState, yesNo } from "./useUrlState";

const FILTERS = {
  search: param.string("", { debounce: 400 }),
  isActive: yesNo(),
  isAvailableOnline: yesNo(),
};

/**
 * The services of one branch, with the list's paging and filter state.
 * `reload()` refetches the current page — mutations call it so the table and the
 * counts stay in step with the API.
 */
export default function useServices(branchId) {
  const { t, i18n } = useTranslation();

  const [services, setServices] = useState([]);
  const [meta, setMeta] = useState(null);
  // Page, filters and search live in the URL — see useUrlState.
  const list = useListUrlState(FILTERS);
  const { page, filters } = list;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { services: list, meta: pagination } = await fetchServices(branchId, {
        page,
        filters,
      });
      setServices(list);
      setMeta(pagination);
    } catch (err) {
      setError(apiErrorMessage(err, t("services.loadFailed")));
      setServices([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [branchId, page, filters, i18n.language, t]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    services,
    meta,
    page,
    setPage: list.setPage,
    filters,
    applyFilters: list.applyFilters,
    resetFilters: list.resetFilters,
    searchTerm: list.searchTerm,
    setSearchTerm: list.setSearchTerm,
    submitSearch: list.submitSearch,
    loading,
    error,
    reload: load,
  };
}
